/**
 * useCustomerProfile — React Query hook around `GET /v1/customers/:id`.
 *
 * Resolves the customer id from the JWT claim (see `lib/auth/jwt.ts`) and
 * passes the bearer through. Disabled when no token / no claim — the gate
 * upstream (`RequireCustomerAuth`) means in practice this hook only runs on
 * pages that already have a usable token, but `enabled: false` keeps tests
 * and SSR-style mounts safe.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { getCustomerById, type CustomerResponse } from '../../lib/api/customer';
import { useCustomerAuth } from '../../lib/auth/useCustomerAuth';

export interface UseCustomerProfileOptions {
  /** Override the api call — used by tests. */
  readonly getCustomerByIdImpl?: typeof getCustomerById;
}

export function customerProfileQueryKey(customerId: string): readonly [string, string] {
  return ['customer-profile', customerId] as const;
}

export function useCustomerProfile(
  options: UseCustomerProfileOptions = {},
): UseQueryResult<CustomerResponse, Error> {
  const { token, claims } = useCustomerAuth();
  const customerId = claims?.customerId ?? '';
  const enabled = customerId.length > 0 && token !== null;
  const impl = options.getCustomerByIdImpl ?? getCustomerById;

  return useQuery({
    queryKey: customerProfileQueryKey(customerId),
    queryFn: () => impl(customerId, { bearerToken: token ?? undefined }),
    staleTime: 60_000,
    enabled,
  });
}
